import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { updateUserSendEmailSchema } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { SEND_UPDATE_USER_EMAIL_ADDRESS_EMAIL_MUTATION } from '../queries';
import { IUser } from '../types';

interface IForm {
  userId: string;
  tenantId: string;
  email: string;
}

const defaultFormValues: IForm = {
  userId: '',
  tenantId: '',
  email: ''
};

interface ChangeEmailProps {
  isOpen: boolean;
  currentUser: IUser;
  onClose: () => void;
}

const ChangeEmail = ({ isOpen, currentUser, onClose }: ChangeEmailProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, getValues, setError, reset, handleSubmit } = useForm<IForm>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(updateUserSendEmailSchema)
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    reset((formValues) => ({ ...formValues, userId: currentUser.id, tenantId: currentUser.tenant.id }));
  }, [currentUser]);

  const [sendUpdateUserEmailAddressEmail, { loading: sendUpdateUserEmailAddressEmailLoading }] = useMutation(
    SEND_UPDATE_USER_EMAIL_ADDRESS_EMAIL_MUTATION
  );

  const onSave = (data: IForm) => {
    setErrorMessage(null);

    sendUpdateUserEmailAddressEmail({
      variables: {
        data
      },
      onCompleted: () => {
        setShowSuccessDialog(true);
      },
      onError: (error) => {
        if (error?.message === 'New user email is already in use') {
          setError('email', { message: 'This email address is already in use' });
          return;
        }
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  if (!currentUser) return <></>;

  if (showSuccessDialog) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={onClose}
        title="Email Sent"
        confirmButton={{
          name: 'OK',
          onClick: onClose
        }}
        showCancelButton={false}
      >
        <ModalBody>
          <svg xmlns="http://www.w3.org/2000/svg" width="132" height="68" viewBox="0 0 132 68" fill="none">
            <g clipPath="url(#clip0_13914_34894)">
              <path
                d="M66.3851 55.8306C56.0876 55.8306 45.8018 55.8306 35.5043 55.8306C32.4541 55.8306 30.1723 54.4001 29.1437 51.87C28.6472 50.6641 28.5526 49.3991 28.8127 48.1459C31.6265 34.3962 34.4639 20.6582 37.3013 6.90846C38.0935 3.03063 41.6639 0.0986126 45.6481 0.0986126C66.4324 0.0867899 87.2048 0.0749672 107.989 0.0986126C112.446 0.0986126 115.284 3.73999 114.385 8.10255C111.583 21.6277 108.805 35.1646 106.026 48.6897C105.163 52.9104 101.664 55.8188 97.3486 55.8188C87.0275 55.8188 76.7063 55.8188 66.3851 55.8188V55.8306ZM108.758 9.58039C108.497 9.75773 108.415 9.81684 108.332 9.87595C97.5378 17.809 86.7555 25.742 75.9614 33.675C72.3555 36.3232 67.8275 35.9095 64.7654 32.67C57.4472 24.9262 50.1171 17.206 42.7871 9.47398C42.6215 9.30847 42.4442 9.15477 42.1959 8.91832C42.1486 9.1193 42.1132 9.21388 42.1013 9.30847C41.5812 11.8622 41.0491 14.4159 40.5289 16.9814C38.3536 27.5863 36.1664 38.1794 33.991 48.7843C33.7191 50.1321 34.1211 50.6168 35.5043 50.6168C56.123 50.6168 76.7417 50.6168 97.3486 50.6168C99.1811 50.6168 100.541 49.4819 100.907 47.6966C103.083 37.1272 105.27 26.5577 107.457 15.9883C107.883 13.9193 108.296 11.8503 108.758 9.58039ZM46.0974 5.3597C46.2393 5.54886 46.2866 5.61979 46.3339 5.67891C53.7585 13.5055 61.1831 21.3321 68.6077 29.1469C69.861 30.471 71.3861 30.5774 72.8876 29.4661C82.9723 22.0533 93.0452 14.6523 103.13 7.2395C103.934 6.64837 104.726 6.04541 105.66 5.3597H46.0974Z"
                fill="#A3A3A3"
              />
              <path
                d="M15.0038 22.6918C19.0472 22.6918 23.0905 22.6918 27.1339 22.6918C28.3753 22.6918 29.3684 23.4366 29.6876 24.5243C30.0068 25.6356 29.6285 26.8297 28.6472 27.409C28.1743 27.6927 27.5713 27.8701 27.0157 27.8819C18.9526 27.9174 10.8895 27.9174 2.83831 27.8937C1.14767 27.8937 -0.0464172 26.7233 -0.0109492 25.2218C0.0245188 23.7321 1.19496 22.6799 2.86196 22.6799C6.90531 22.6799 10.9487 22.6799 14.992 22.6799L15.0038 22.6918Z"
                fill="#A3A3A3"
              />
              <path
                d="M22.5214 11.8977C19.9322 11.8977 17.3549 11.8977 14.7657 11.8977C13.4652 11.8977 12.5431 11.2356 12.1529 10.1361C11.7982 9.10756 12.0583 7.96076 12.9568 7.32234C13.4416 6.97948 14.1036 6.70756 14.683 6.70756C19.8731 6.66027 25.0751 6.67209 30.2652 6.68391C31.8495 6.68391 32.9844 7.80707 32.9844 9.2849C32.9844 10.7627 31.8613 11.8622 30.2652 11.8859C27.6761 11.9095 25.0987 11.8859 22.5096 11.8859L22.5214 11.8977Z"
                fill="#A3A3A3"
              />
              <path
                d="M17.2251 37.0798C19.1167 37.0798 21.0201 37.0798 22.9118 37.0798C24.6379 37.0798 25.7728 38.132 25.7728 39.6926C25.7728 41.2414 24.6142 42.3054 22.8999 42.3054C19.0812 42.3054 15.2625 42.3054 11.4438 42.3054C9.80042 42.3054 8.61816 41.2178 8.59451 39.7281C8.55904 38.2148 9.76496 37.0917 11.4438 37.0798C13.3709 37.0798 15.2861 37.0798 17.2132 37.0798H17.2251Z"
                fill="#A3A3A3"
              />
              <path
                d="M111.04 67.9134C122.616 67.9134 132.001 58.5286 132.001 46.9518C132.001 35.3751 122.616 25.9902 111.04 25.9902C99.4629 25.9902 90.0781 35.3751 90.0781 46.9518C90.0781 58.5286 99.4629 67.9134 111.04 67.9134Z"
                fill="white"
              />
              <path
                d="M124.763 43.7358C125.011 44.7762 125.129 45.8284 125.129 46.8925C125.129 50.0137 124.101 53.0639 122.186 55.5348C120.27 58.0058 117.598 59.7792 114.572 60.5595C111.545 61.3398 108.341 61.0915 105.468 59.8619C102.595 58.6206 100.219 56.4688 98.7057 53.726C97.1924 50.9831 96.6367 47.8265 97.1333 44.7408C97.618 41.655 99.1195 38.8176 101.401 36.6777C103.683 34.5378 106.603 33.2137 109.713 32.9181C112.822 32.6225 115.943 33.3674 118.591 35.0462L121.121 32.5161C118.178 30.4472 114.666 29.3476 111.072 29.3595C107.324 29.3595 103.683 30.5417 100.656 32.7408C97.6298 34.9516 95.3954 38.0609 94.2604 41.6314C93.1254 45.2018 93.1609 49.0442 94.355 52.591C95.549 56.1378 97.8545 59.2117 100.917 61.3634C103.979 63.5151 107.655 64.6383 111.391 64.5555C115.139 64.4728 118.757 63.2077 121.736 60.9378C124.716 58.656 126.879 55.4994 127.931 51.9053C128.984 48.3112 128.865 44.4807 127.577 40.9575L124.751 43.7476L124.763 43.7358Z"
                fill="#E5E5E5"
              />
              <path
                d="M128.644 34.8808L108.616 54.9084L101.168 47.4601L103.651 44.9774L108.628 49.9547L126.185 32.3862L128.656 34.8808H128.644Z"
                fill="#434343"
              />
            </g>
            <defs>
              <clipPath id="clip0_13914_34894">
                <rect width="132" height="67.8266" fill="white" transform="translate(0 0.0866699)" />
              </clipPath>
            </defs>
          </svg>
          <ModalDescription>Your new link has been sent to:</ModalDescription>
          <UserEmail>{getValues('email')}</UserEmail>
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Change Email Address"
      confirmButton={{
        name: 'Continue',
        onClick: handleSubmit(onSave),
        disabled: sendUpdateUserEmailAddressEmailLoading
      }}
      showCloseIcon={!sendUpdateUserEmailAddressEmailLoading}
      showCancelButton={!sendUpdateUserEmailAddressEmailLoading}
      isUseOutsideClick={!sendUpdateUserEmailAddressEmailLoading}
    >
      {sendUpdateUserEmailAddressEmailLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalBody>
            <div>
              <ModalDescription>Are you sure you want to change the current email address?</ModalDescription>
              <UserEmail>{currentUser.email}</UserEmail>
              <br />
              <ModalDescription>Enter the new email address below. We will send a verification code to that email address.</ModalDescription>
            </div>
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input label="New Email Address" value={value || ''} onChange={onChange} errorText={error?.message} isRequiredField size="md" />
              )}
            />
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default ChangeEmail;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalDescription = styled.div`
  font-size: 16px;
  text-align: center;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const UserEmail = styled.p`
  font-size: 19px;
  font-weight: 700;
  text-align: center;
  line-height: 28px;
  color: ${themeVar('font.base')};
`;
